/**
 * Account → Change password (AUTHED). Verifies the current password, sets a new one, and signs out
 * every OTHER device (this one gets a fresh session and stays signed in). Reachable from the
 * Sessions & security screen.
 *
 * Design-system primitives only; never imports @smartbox/* (mobile invariant 1).
 */
import { useState } from 'react';
import { KeyboardAvoidingView, Platform, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useSession } from '../../../../src/auth/session';
import { ApiRequestError } from '../../../../src/api/client';
import { Screen, Text, Field, Button, Card } from '../../../../src/components/ui';
import { theme } from '../../../../src/theme';

export default function ChangePasswordScreen() {
  const changePassword = useSession((s) => s.changePassword);
  const router = useRouter();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function onSubmit() {
    setError(null);
    if (!current) return setError('Enter your current password.');
    if (next.length < 8) return setError('New password must be at least 8 characters.');
    if (next !== confirm) return setError('New passwords do not match.');
    if (next === current) return setError('Choose a password different from your current one.');
    setBusy(true);
    try {
      await changePassword(current, next);
      setDone(true);
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : 'Could not change your password. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <Screen scroll>
        <Card>
          <Text variant="bodyStrong">Password changed</Text>
          <Text variant="small" color="secondary">
            Your password has been updated and every other device has been signed out. You're still
            signed in here.
          </Text>
          <Button title="Done" onPress={() => router.back()} accessibilityLabel="Done" />
        </Card>
      </Screen>
    );
  }

  return (
    <Screen scroll>
      <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <Text variant="body" color="secondary">
          Changing your password signs you out of every other device.
        </Text>
        <View style={styles.form}>
          <Field
            label="Current password"
            value={current}
            onChangeText={setCurrent}
            secureToggle
            autoCapitalize="none"
            textContentType="password"
            autoComplete="current-password"
            placeholder="Current password"
            editable={!busy}
            accessibilityLabel="Current password"
            returnKeyType="next"
          />
          <Field
            label="New password"
            value={next}
            onChangeText={setNext}
            secureToggle
            autoCapitalize="none"
            textContentType="newPassword"
            autoComplete="new-password"
            placeholder="At least 8 characters"
            editable={!busy}
            accessibilityLabel="New password"
            returnKeyType="next"
          />
          <Field
            label="Confirm new password"
            value={confirm}
            onChangeText={setConfirm}
            secureToggle
            autoCapitalize="none"
            textContentType="newPassword"
            autoComplete="new-password"
            placeholder="Repeat new password"
            editable={!busy}
            accessibilityLabel="Confirm new password"
            returnKeyType="done"
            onSubmitEditing={onSubmit}
            error={error ?? undefined}
          />
          <Button
            title="Change password"
            onPress={onSubmit}
            loading={busy}
            disabled={busy}
            size="lg"
            style={styles.submit}
            accessibilityLabel="Change password"
          />
        </View>
      </KeyboardAvoidingView>
    </Screen>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  form: { gap: theme.spacing.md, marginTop: theme.spacing.md },
  submit: { marginTop: theme.spacing.sm },
});
